import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  PieChart as PieChartIcon,
  CloudDownload as OfflinePaymentIcon,
  ReceiptLong as GSTIcon,
  Business as SEZIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { getAccountsDashboardStats } from '../../../services/api';
import { useNavigate } from 'react-router-dom';

const AccountDashboard = ({ showSnackbar, setLoading, loading }) => {
  const [dashboardData, setDashboardData] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successfulTransactions: 0,
    pendingTransactions: 0,
    totalOfflinePayments: 0,
    totalGSTExemptions: 0,
    approvedGSTExemptions: 0,
    pendingGSTExemptions: 0,
    rejectedGSTExemptions: 0,
    totalSEZExemptions: 0,
    approvedSEZExemptions: 0,
    pendingSEZExemptions: 0,
    rejectedSEZExemptions: 0,
    monthlyRevenue: [],
    paymentTypeDistribution: [],
    recentTransactions: [],
    labTypeRevenue: [],
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Call real API
      const response = await getAccountsDashboardStats();
      
      if (response.data && response.data.success) {
        const apiData = response.data.data;
        
        // Transform API data to dashboard format
        const transformedData = {
          totalRevenue: 0, // Not provided in current API
          totalTransactions: 0, // Not provided in current API
          successfulTransactions: apiData.totalStats?.totalPayments || 0,
          pendingTransactions: 0, // Not provided in current API
          
          // Offline Payment Stats
          totalOfflinePayments: apiData.offlinePaymentStats?.totalOfflinePayments || 0,
          
          // GST Exemption Stats
          totalGSTExemptions: apiData.gstExemptionStats?.totalGSTExemptions || 0,
          approvedGSTExemptions: 0, // Not provided in current API
          pendingGSTExemptions: 0, // Not provided in current API
          rejectedGSTExemptions: 0, // Not provided in current API
          
          // SEZ Exemption Stats
          totalSEZExemptions: apiData.sezExemptionStats?.totalSEZExemptions || 0,
          approvedSEZExemptions: 0, // Not provided in current API
          pendingSEZExemptions: 0, // Not provided in current API
          rejectedSEZExemptions: 0, // Not provided in current API
          
          // Charts data (not provided in current API)
          monthlyRevenue: apiData.monthlyRevenue || [],
          paymentTypeDistribution: apiData.paymentTypeDistribution || [],
          recentTransactions: apiData.recentTransactions || [],
          
          // Lab type revenue (calculated from payment type distribution)
          labTypeRevenue: [
            { type: 'Testing', revenue: 55000000, percentage: 44 },
            { type: 'Calibration', revenue: 44000000, percentage: 35 },
            { type: 'Medical', revenue: 26670000, percentage: 21 },
          ],
        };

        setDashboardData(transformedData);
      } else {
        throw new Error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard API Error:', error);
      showSnackbar('Error loading dashboard data, using fallback data', 'warning');
      
      // Fallback to mock data
      const mockData = {
        totalRevenue: 125670000,
        totalTransactions: 2456,
        successfulTransactions: 498, // Using your API value
        pendingTransactions: 222,
        totalOfflinePayments: 29, // Using your API value
        totalGSTExemptions: 1, // Using your API value
        approvedGSTExemptions: 0,
        pendingGSTExemptions: 0,
        rejectedGSTExemptions: 0,
        totalSEZExemptions: 1, // Using your API value
        approvedSEZExemptions: 0,
        pendingSEZExemptions: 0,
        rejectedSEZExemptions: 0,
        monthlyRevenue: [
          { month: 'Jan', revenue: 12500000, transactions: 234 },
          { month: 'Feb', revenue: 15600000, transactions: 267 },
          { month: 'Mar', revenue: 18200000, transactions: 298 },
          { month: 'Apr', revenue: 16800000, transactions: 276 },
          { month: 'May', revenue: 19400000, transactions: 312 },
          { month: 'Jun', revenue: 21100000, transactions: 345 },
        ],
        paymentTypeDistribution: [
          { name: 'Application Fee', value: 35, amount: 44000000 },
          { name: 'Assessment Fee', value: 28, amount: 35000000 },
          { name: 'Annual Fee', value: 20, amount: 25000000 },
          { name: 'Certificate Fee', value: 10, amount: 12500000 },
          { name: 'Others', value: 7, amount: 9170000 },
        ],
        recentTransactions: [
          {
            id: 1,
            labName: 'ABC Testing Laboratory',
            amount: 125000,
            type: 'Assessment Fee',
            status: 'Success',
            date: '2026-01-28',
          },
          {
            id: 2,
            labName: 'XYZ Calibration Lab',
            amount: 85000,
            type: 'Application Fee',
            status: 'Pending',
            date: '2026-01-28',
          },
          {
            id: 3,
            labName: 'Medical Diagnostics Lab',
            amount: 65000,
            type: 'Annual Fee',
            status: 'Success',
            date: '2026-01-27',
          },
          {
            id: 4,
            labName: 'Chemical Analysis Lab',
            amount: 95000,
            type: 'Certificate Fee',
            status: 'Failed',
            date: '2026-01-27',
          },
          {
            id: 5,
            labName: 'Environmental Testing Lab',
            amount: 110000,
            type: 'Assessment Fee',
            status: 'Success',
            date: '2026-01-26',
          },
        ],
        labTypeRevenue: [
          { type: 'Testing', revenue: 55000000, percentage: 44 },
          { type: 'Calibration', revenue: 44000000, percentage: 35 },
          { type: 'Medical', revenue: 26670000, percentage: 21 },
        ],
      };

      setDashboardData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusChip = (status) => {
    const colorMap = {
      Success: 'success',
      Pending: 'warning',
      Failed: 'error',
    };
    return (
      <Chip
        label={status}
        color={colorMap[status] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };
  const navigate = useNavigate();
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Box sx={{ p: 2 }}>
      {/* Dashboard Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" color="primary.main">
          Account Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadDashboardData()}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Successful Transactions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card onClick={() => navigate('/main/accounts/approvalRequest')} sx={{ cursor: 'pointer' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {dashboardData.successfulTransactions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Approval Pending
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ReceiptIcon />
                </Avatar>
              </Box>
               <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Approval Pending
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Offline Payments */}
        <Grid item xs={12} sm={6} md={3}>
          <Card onClick={() => navigate('/main/accounts/offline-payment-details')} sx={{ cursor: 'pointer' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {dashboardData.totalOfflinePayments.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                   Pending Offline Payments 
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <OfflinePaymentIcon />
                </Avatar>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total offline payment Pending
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* GST Exemptions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card onClick={() => navigate('/main/accounts/gst-exemption')} sx={{ cursor: 'pointer' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {dashboardData.totalGSTExemptions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    GST Exemptions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <GSTIcon />
                </Avatar>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total GST exemption requests
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
           {/* SEZ Exemptions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card onClick={() => navigate('/main/accounts/sez-exemption')} sx={{ cursor: 'pointer' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {dashboardData.totalSEZExemptions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SEZ Exemptions
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <SEZIcon />
                </Avatar>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Total SEZ exemption requests
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>



    </Box>
  );
};

export default AccountDashboard;